import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "../components/PageHeader";
import useEditableContent from "../hooks/useEditableContent";
import internationalProgrammes from "../data/internationalProgrammes";

const FALLBACK_PROGRAMMES = [];

const formatPrice = (price) => String(price || "").replace(/\s/g, ",");

const uiCopy = {
  ar: {
    notFoundTitle: "البرنامج غير موجود",
    notFoundSubtitle: "يمكنك العودة إلى صفحة الباقات واختيار برنامج آخر.",
    backPackages: "العودة للباقات",
    bookNow: "احجز الآن",
    whatsapp: "تواصل عبر واتساب",
    from: "ابتداء من",
    duration: "المدة",
    days: "يوم",
    route: "المسار",
    dates: "التواريخ",
    airline: "الطيران",
    internationalTrip: "رحلة خارجية",
    tripPhotos: "صور الرحلة",
    highlights: "أبرز اللحظات",
    selectedHotels: "الفنادق المختارة",
    dailyProgram: "البرنامج اليومي",
    pricesByMonth: "أسعار حسب الشهر",
    monthDate: "الشهر / التاريخ",
    price: "السعر",
    included: "الخدمات المشمولة",
    notIncluded: "غير مشمول",
    notes: "ملاحظات هامة",
    hotelCategory: "تصنيف الفندق",
    makkahHotel: "فندق مكة",
    madinahHotel: "فندق المدينة",
    hotelPhotos: "صور الفندق",
    hotelInfo: "معلومات الفندق",
    category: "التصنيف",
    makkah: "مكة",
    madinah: "المدينة",
    meals: "الوجبات",
    roomPrices: "أسعار الغرف",
    roomType: "نوع الغرفة",
    personPrice: "السعر للفرد",
    faq: "أسئلة شائعة",
    whatsappMessage: "السلام عليكم، أريد معلومات أكثر حول",
  },
  fr: {
    notFoundTitle: "Programme introuvable",
    notFoundSubtitle: "Retournez aux forfaits et choisissez un autre programme.",
    backPackages: "Retour aux forfaits",
    bookNow: "Réserver",
    whatsapp: "Contacter sur WhatsApp",
    from: "À partir de",
    duration: "Durée",
    days: "jours",
    route: "Itinéraire",
    dates: "Dates",
    airline: "Vol",
    internationalTrip: "Voyage international",
    tripPhotos: "Photos du voyage",
    highlights: "Temps forts",
    selectedHotels: "Hôtels sélectionnés",
    dailyProgram: "Programme jour par jour",
    pricesByMonth: "Prix selon la date",
    monthDate: "Mois / date",
    price: "Prix",
    included: "Inclus",
    notIncluded: "Non inclus",
    notes: "Notes importantes",
    hotelCategory: "Catégorie hôtel",
    makkahHotel: "Hôtel à Makkah",
    madinahHotel: "Hôtel à Madinah",
    hotelPhotos: "Photos de l'hôtel",
    hotelInfo: "Informations hôtel",
    category: "Catégorie",
    makkah: "Makkah",
    madinah: "Madinah",
    meals: "Repas",
    roomPrices: "Tarifs des chambres",
    roomType: "Type de chambre",
    personPrice: "Prix par personne",
    faq: "FAQ",
    whatsappMessage: "Bonjour, je souhaite recevoir plus d'informations sur",
  },
  en: {
    notFoundTitle: "Program Not Found",
    notFoundSubtitle: "You can return to packages and choose another program.",
    backPackages: "Back to Packages",
    bookNow: "Book Now",
    whatsapp: "Contact on WhatsApp",
    from: "From",
    duration: "Duration",
    days: "days",
    route: "Route",
    dates: "Dates",
    airline: "Flight",
    internationalTrip: "International Trip",
    tripPhotos: "Trip Photos",
    highlights: "Highlights",
    selectedHotels: "Selected Hotels",
    dailyProgram: "Daily Program",
    pricesByMonth: "Prices by Date",
    monthDate: "Month / date",
    price: "Price",
    included: "Included Services",
    notIncluded: "Not Included",
    notes: "Important Notes",
    hotelCategory: "Hotel Category",
    makkahHotel: "Makkah Hotel",
    madinahHotel: "Madinah Hotel",
    hotelPhotos: "Hotel Photos",
    hotelInfo: "Hotel Information",
    category: "Category",
    makkah: "Makkah",
    madinah: "Madinah",
    meals: "Meals",
    roomPrices: "Room Prices",
    roomType: "Room Type",
    personPrice: "Price per Person",
    faq: "FAQ",
    whatsappMessage: "Hello, I would like more information about",
  },
};

const localizedProgramCopy = {
  101: {
    fr: { title: "Vietnam + Thaïlande", route: "Hanoï → Da Nang → Bangkok", location: "Hanoï, Da Nang, Bangkok", badge: "Asie", type: "Voyage international", airline: "Turkish Airlines - escale à Istanbul", summary: "Un voyage asiatique riche entre la culture du Vietnam, la nature de Da Nang et l'énergie de Bangkok, avec hôtels 4 étoiles et vols Turkish Airlines." },
    en: { title: "Vietnam + Thailand", route: "Hanoi → Da Nang → Bangkok", location: "Hanoi, Da Nang, Bangkok", badge: "Asia", type: "International Trip", airline: "Turkish Airlines - Istanbul stopover", summary: "A rich Asian journey combining Vietnam's culture, Da Nang's scenery, and Bangkok's energy with 4-star hotels and Turkish Airlines flights." },
  },
  102: {
    fr: { title: "Thaïlande et îles Phi Phi", route: "Bangkok → Phuket → Îles Phi Phi", location: "Bangkok, Phuket, Phi Phi", badge: "Îles et plages", type: "Voyage international", summary: "Un programme en Thaïlande entre Bangkok, Phuket et les îles Phi Phi avec sorties en mer, aventure et hôtels sélectionnés." },
    en: { title: "Thailand and Phi Phi Islands", route: "Bangkok → Phuket → Phi Phi Islands", location: "Bangkok, Phuket, Phi Phi", badge: "Islands and Beaches", type: "International Trip", summary: "A Thailand program combining Bangkok, Phuket, and Phi Phi Islands with sea tours, adventure experiences, and selected hotels." },
  },
  103: {
    fr: { title: "Kuala Lumpur + Bali", route: "Kuala Lumpur → Bali → Kuala Lumpur", location: "Malaisie, Bali", badge: "Malaisie et Bali", type: "Voyage international", summary: "Un long séjour riche en activités entre Kuala Lumpur et Bali: plages, Ubud, Kuta, Seminyak et excursions variées." },
    en: { title: "Kuala Lumpur + Bali", route: "Kuala Lumpur → Bali → Kuala Lumpur", location: "Malaysia, Bali", badge: "Malaysia and Bali", type: "International Trip", summary: "A longer activity-filled journey between Kuala Lumpur and Bali: beaches, Ubud, Kuta, Seminyak, and rich excursions." },
  },
  104: {
    fr: { title: "Maldives + Sri Lanka + Istanbul", route: "Maldives → Sri Lanka → Istanbul", location: "Maldives, Sri Lanka, Istanbul", badge: "Hôtels 5 étoiles", type: "Voyage international", airline: "Vol international multi-destinations", summary: "Un programme haut de gamme entre détente aux Maldives, nature et aventure au Sri Lanka, puis une touche finale à Istanbul." },
    en: { title: "Maldives + Sri Lanka + Istanbul", route: "Maldives → Sri Lanka → Istanbul", location: "Maldives, Sri Lanka, Istanbul", badge: "5-Star Hotels", type: "International Trip", airline: "Multi-destination international flight", summary: "A premium program combining Maldives relaxation, Sri Lanka nature and adventure, and a final Istanbul stop." },
  },
  105: {
    fr: { title: "Istanbul", route: "Casablanca → Istanbul → Casablanca", location: "Istanbul", badge: "3 sorties gratuites", type: "Voyage international", airline: "Royal Air Maroc", summary: "Programme Istanbul de 8 jours avec hôtel 4 étoiles, Royal Air Maroc, 56 kg de bagages et trois sorties programmées gratuitement." },
    en: { title: "Istanbul", route: "Casablanca → Istanbul → Casablanca", location: "Istanbul", badge: "3 Free Tours", type: "International Trip", airline: "Royal Air Maroc", summary: "An 8-day Istanbul program with a 4-star hotel, Royal Air Maroc flights, 56 kg baggage allowance, and three included tours." },
  },
  106: {
    fr: { title: "Égypte: Le Caire + Alexandrie + Charm el-Cheikh", route: "Le Caire → Alexandrie → Charm el-Cheikh", location: "Le Caire, Alexandrie, Charm el-Cheikh", badge: "Égypte", type: "Voyage international", airline: "Vols directs et internes", summary: "Un voyage en Égypte entre les monuments du Caire, une journée à Alexandrie et un séjour balnéaire tout compris à Charm el-Cheikh." },
    en: { title: "Egypt: Cairo + Alexandria + Sharm El Sheikh", route: "Cairo → Alexandria → Sharm El Sheikh", location: "Cairo, Alexandria, Sharm El Sheikh", badge: "Egypt", type: "International Trip", airline: "Direct international and domestic flights", summary: "An Egyptian journey combining Cairo's monuments, an Alexandria day trip, and an all-inclusive beach stay in Sharm El Sheikh." },
  },
};

const internationalDetailCopy = {
  fr: {
    101: {
      title: "Vietnam + Thaïlande",
      route: "Hanoï → Da Nang → Bangkok",
      location: "Hanoï, Da Nang, Bangkok",
      dates: "20/06 au 05/07",
      badge: "Asie",
      type: "Voyage international",
      airline: "Turkish Airlines - escale à Istanbul",
      summary: "Un voyage asiatique riche entre la culture du Vietnam, la nature de Da Nang et l'énergie de Bangkok, avec hôtels 4 étoiles et vols Turkish Airlines.",
      hotels: [
        { city: "Hanoï", name: "Rey Hanoi Hotel", rating: "4.5★", nights: "" },
        { city: "Da Nang", name: "Balcona Danang", rating: "4★", nights: "" },
        { city: "Bangkok", name: "Grand Mercure Bangkok Atrium", rating: "4.3★", nights: "" },
      ],
      itinerary: [
        { day: "Jour 1", title: "Départ", items: ["Rassemblement à l'aéroport Mohammed V de Casablanca", "Escale à Istanbul puis arrivée à l'aéroport de Hanoï"] },
        { day: "Jour 2", date: "21/06", title: "Arrivée à Hanoï", items: ["Accueil à l'aéroport", "Transfert à l'hôtel et installation", "Temps libre pour se reposer"] },
        { day: "Jour 3", date: "22/06", title: "Visite de Hanoï", items: ["Petit-déjeuner à l'hôtel", "Journée complète de découverte de Hanoï", "Visite des principaux monuments et marchés traditionnels"] },
        { day: "Jour 4", date: "23/06", title: "Excursion à Ninh Binh", items: ["Journée complète entre nature et paysages montagneux", "Retour à Hanoï et nuit à l'hôtel"] },
        { day: "Jour 5", date: "24/06", title: "Baie d'Ha Long", items: ["Départ vers Ha Long Bay", "Croisière haut de gamme et sortie en mer", "Déjeuner inclus"] },
        { day: "Jour 6", date: "25/06", title: "Journée libre", items: ["Repos et détente à l'hôtel", "Possibilité d'activités optionnelles"] },
        { day: "Jour 7", date: "26/06", title: "Départ vers Da Nang", items: ["Transfert à l'aéroport", "Arrivée à Da Nang", "Accueil et transfert à l'hôtel"] },
        { day: "Jour 8", date: "27/06", title: "Visite de Da Nang", items: ["Son Tra Peninsula", "Linh Ung Pagoda", "Marble Mountains", "Han Market", "Temps libre pour le spectacle Fireworks"] },
        { day: "Jour 9", date: "28/06", title: "Da Nang de nuit", items: ["Promenade au marché de nuit", "Spectacle du Dragon Bridge"] },
        { day: "Jour 10", date: "29/06", title: "Ba Na Hills", items: ["Montée en téléphérique", "Visite du Golden Bridge", "Village français", "Déjeuner buffet dans un restaurant halal"] },
        { day: "Jour 11", date: "30/06", title: "Hoi An", items: ["Coconut Forest", "Hoi An Ancient Town", "Hoi An Night Market", "Japanese Covered Bridge"] },
        { day: "Jour 12", date: "01/07", title: "Journée libre", items: ["Journée libre avec activités possibles selon la météo"] },
        { day: "Jour 13", date: "02/07", title: "Départ vers Bangkok", items: ["Transfert de l'hôtel à l'aéroport", "Arrivée à Bangkok", "Temps libre en soirée"] },
        { day: "Jour 14", date: "03/07", title: "Bangkok", items: ["Journée libre au centre-ville", "Soirée libre pour découvrir la ville"] },
        { day: "Jour 15", date: "04/07", title: "Départ", items: ["Check-out à 12:00", "Transfert vers l'aéroport international de Bangkok"] },
        { day: "Jour 16", date: "05/07", title: "Arrivée au Maroc", items: ["Arrivée au Maroc"] },
      ],
      includes: ["Billet aller-retour avec Turkish Airlines", "Vol Hanoï vers Da Nang", "Vol Da Nang vers Bangkok", "Hôtels avec petit-déjeuner", "Visites et activités mentionnées", "3 déjeuners", "Frais de visa Vietnam"],
      notIncluded: ["Repas et dépenses personnelles", "Activités optionnelles", "Assurance voyage"],
    },
    102: {
      title: "Thaïlande et îles Phi Phi",
      route: "Bangkok → Phuket → Îles Phi Phi",
      location: "Bangkok, Phuket, Phi Phi",
      dates: "Dates mensuelles d'avril à octobre",
      badge: "Îles et plages",
      type: "Voyage international",
      summary: "Un programme en Thaïlande entre Bangkok, Phuket et les îles Phi Phi avec sorties en mer, aventure et hôtels sélectionnés.",
      hotels: [
        { city: "Bangkok", name: "Grand Mercure Bangkok Atrium", rating: "4.3★" },
        { city: "Phuket", name: "Novotel Phuket Vintage Park Resort", rating: "4.4★" },
        { city: "Phuket", name: "Nap Patong / Deevana Plaza", rating: "4.4★ - 4.6★" },
      ],
      priceTable: [
        { label: "Avril", value: "21,500 DH" },
        { label: "Mai", value: "21,500 DH" },
        { label: "Juin", value: "25,000 DH" },
        { label: "Juillet", value: "27,000 DH" },
        { label: "Août", value: "28,000 DH" },
        { label: "Septembre", value: "28,000 DH" },
        { label: "Octobre", value: "25,000 DH" },
      ],
      itinerary: [
        { day: "Jour 1", title: "Arrivée à Bangkok", items: ["Accueil à l'aéroport", "Transfert privé à l'hôtel", "Soirée libre"] },
        { day: "Jour 2", title: "Découverte de Bangkok", items: ["Temple du Bouddha", "Palais royal", "Centre commercial haut de gamme", "Marché de nuit traditionnel"] },
        { day: "Jour 3", title: "Journée libre", items: ["Possibilité de croisière sur le Chao Phraya", "Shopping ou activités optionnelles"] },
        { day: "Jour 4", title: "Départ vers Phuket", items: ["Transfert à l'aéroport", "Vol intérieur vers Phuket", "Accueil et transfert à l'hôtel"] },
        { day: "Jour 5", title: "Incontournables de Phuket", items: ["Vieille ville", "Wat Chalong", "Big Buddha", "Karon View Point"] },
        { day: "Jour 6", title: "Journée aventure", items: ["Safari", "Rafting", "Tyrolienne", "Quad", "Rencontre avec les éléphants", "Déjeuner inclus"] },
        { day: "Jour 7", title: "Journée libre", items: ["Plage ou activités nautiques optionnelles"] },
        { day: "Jour 8", title: "Îles Phi Phi", items: ["Transfert en speed boat", "Temps libre sur l'île"] },
        { day: "Jour 9", title: "Sortie en mer privée", items: ["Monkey Beach", "Piley Lagoon", "Viking Cave", "Maya Bay", "Déjeuner inclus"] },
        { day: "Jour 10", title: "Retour à Phuket", items: ["Retour en bateau", "Soirée libre"] },
        { day: "Jour 11", title: "Départ", items: ["Transfert privé à l'aéroport", "Vol retour vers le Maroc"] },
      ],
      includes: ["Vol Casablanca vers Bangkok/Phuket et retour", "Vol intérieur Bangkok vers Phuket", "10 nuits dans les hôtels", "Petits-déjeuners", "2 déjeuners", "Billets bateau aller-retour", "Visites mentionnées", "Guide et accompagnement", "Autocar climatisé"],
      notIncluded: ["Repas et dépenses personnelles", "Activités des journées libres", "Assurance voyage"],
    },
    103: {
      title: "Kuala Lumpur + Bali",
      route: "Kuala Lumpur → Bali → Kuala Lumpur",
      location: "Malaisie, Bali",
      dates: "Deux départs par mois d'avril à novembre 2026",
      badge: "Malaisie et Bali",
      type: "Voyage international",
      summary: "Un long séjour riche en activités entre Kuala Lumpur et Bali: plages, Ubud, Kuta, Seminyak et excursions variées.",
      hotels: [
        { city: "Kuala Lumpur", name: "Royal Signature Bukit Bintang", rating: "5★", nights: "4 nuits" },
        { city: "Bali - Kuta", name: "Discovery Kartika Plaza", rating: "5★", nights: "3 nuits" },
        { city: "Bali - Ubud", name: "Swan Paradise", rating: "4★", nights: "4 nuits" },
        { city: "Bali - Seminyak", name: "Jambuluwuk Oceano", rating: "5★", nights: "2 nuits" },
      ],
      priceTable: [
        { label: "Avril", value: "21,400 DH" },
        { label: "Mai", value: "22,900 DH" },
        { label: "Juin", value: "23,900 DH" },
        { label: "Juillet", value: "26,300 DH" },
        { label: "Août", value: "26,800 DH" },
        { label: "Septembre", value: "26,300 / 25,300 DH" },
        { label: "Octobre", value: "22,900 DH" },
        { label: "Novembre", value: "21,400 DH" },
      ],
      itinerary: [
        { day: "Jour 1", title: "Départ", items: ["Rassemblement à l'aéroport Mohammed V", "Vol international vers Kuala Lumpur"] },
        { day: "Jour 2", title: "Arrivée à Kuala Lumpur", items: ["Accueil local", "Transfert à l'hôtel", "Sortie de nuit vers les centres commerciaux et les tours Petronas"] },
        { day: "Jour 3", title: "Visite de la capitale", items: ["Batu Caves", "Temple chinois", "Mosquée nationale", "Place de l'Indépendance"] },
        { day: "Jour 4", title: "Départ vers Bali", items: ["Vol AirAsia ou BatikAir", "Accueil avec collier de fleurs", "Nuit à Kuta"] },
        { day: "Jour 6", title: "Sud de Bali", items: ["Tanjung Benoa", "Banana Boat", "Melasti Beach", "Spectacle Kecak", "Déjeuner inclus"] },
        { day: "Jour 7", title: "Villages d'Ubud", items: ["Glass Bridge Bali", "Rainbow Slide", "Peliatan Royal Palace", "Taman Dedari", "Plantations de café"] },
        { day: "Jour 9", title: "Activités à Ubud", items: ["Rafting sur la rivière Ayung", "D'Tukad Coffee Club", "Aloha Swing"] },
        { day: "Jour 11", title: "Bedugul et Seminyak", items: ["Ulun Danu Beratan Temple", "Handara Golf Resort", "Wanagiri Hidden Hill"] },
        { day: "Jour 12", title: "Îles optionnelles", items: ["Nusa Penida ou Nusa Lembongan", "Plages, vues panoramiques et déjeuner selon l'excursion"] },
        { day: "Jour 15", title: "Retour au Maroc", items: ["Départ de l'hôtel", "Transfert à l'aéroport", "Retour au Maroc"] },
      ],
      includes: ["Vol international aller-retour", "Vol intérieur Kuala Lumpur - Bali aller-retour", "13 nuits dans les hôtels", "Petits-déjeuners", "Visites et activités mentionnées", "Accompagnateur parlant arabe ou darija marocaine"],
      notIncluded: ["Dépenses personnelles", "Certaines activités optionnelles", "Tout service non mentionné"],
      notes: ["Avance: 10,000 DH, et 15,000 DH en juillet, août et septembre", "Solde à régler 10 jours avant le voyage", "L'annulation n'est pas possible après émission du billet d'avion"],
    },
    104: {
      title: "Maldives + Sri Lanka + Istanbul",
      route: "Maldives → Sri Lanka → Istanbul",
      location: "Maldives, Sri Lanka, Istanbul",
      dates: "30/04 au 10/05",
      badge: "Hôtels 5 étoiles",
      type: "Voyage international",
      airline: "Vol international multi-destinations",
      summary: "Un programme haut de gamme entre détente aux Maldives, nature et aventure au Sri Lanka, puis une touche finale à Istanbul.",
      hotels: [{ city: "Toutes les destinations", name: "Hôtels sélectionnés 5 étoiles", rating: "5★", nights: "" }],
      itinerary: [
        { day: "Jour 1", title: "Arrivée aux Maldives", items: ["Arrivée à l'aéroport international des Maldives", "Speed boat vers l'île MARINA", "Temps libre"] },
        { day: "Jours 2 à 4", title: "Journées libres sur l'île", items: ["Profiter de l'île, de la plage et du resort"] },
        { day: "Jour 5", title: "Départ vers le Sri Lanka", items: ["Speed boat vers l'aéroport", "Arrivée au Sri Lanka", "Visite de Colombo"] },
        { day: "Jour 6", title: "Colombo vers Dambulla", items: ["MAHA RAJA ELEPHANT VILLAGE", "Tuk Tuk", "Arrivée à l'hôtel à Dambulla"] },
        { day: "Jour 7", title: "Habarana et safari", items: ["Habarana Old Village Tour", "Bullock Cart Ride", "Lake Rafting", "Safari parmi les éléphants"] },
        { day: "Jour 8", title: "Dambulla vers Colombo", items: ["Ayurvedic Herbal Garden", "Kandy Lake", "Gem Museum"] },
        { day: "Jour 9", title: "Colombo", items: ["Visite de la ville de Colombo"] },
        { day: "Jour 10", title: "Istanbul", items: ["Transfert de l'aéroport à l'hôtel", "Temps libre"] },
        { day: "Jour 11", title: "Retour au Maroc", items: ["Vol retour"] },
      ],
      includes: ["Hôtels 5 étoiles", "Petit-déjeuner et dîner", "Transferts mentionnés", "Visites mentionnées dans le programme"],
      notIncluded: ["Dépenses personnelles", "Activités optionnelles", "Tout service non mentionné"],
    },
    105: {
      title: "Istanbul",
      route: "Casablanca → Istanbul → Casablanca",
      location: "Istanbul",
      dates: "Plusieurs dates d'avril à juillet",
      badge: "3 sorties gratuites",
      type: "Voyage international",
      airline: "Royal Air Maroc",
      summary: "Programme Istanbul de 8 jours avec hôtel 4 étoiles, Royal Air Maroc, 56 kg de bagages et trois sorties programmées gratuitement.",
      hotels: [{ city: "Istanbul", name: "Grand Asiyan Hotel", rating: "4★", location: "Laleli, à 200 m du tramway, piscine intérieure et hammam turc" }],
      priceTable: [
        { label: "12/04 → 19/04", value: "7,990 DH" },
        { label: "25/04 → 02/05", value: "8,290 DH" },
        { label: "01/05 → 08/05", value: "8,700 DH" },
        { label: "02/05 → 09/05", value: "8,900 DH" },
        { label: "17/05 → 24/05", value: "8,190 DH" },
        { label: "21/06 → 28/06", value: "10,390 DH" },
        { label: "28/06 → 05/07", value: "10,390 DH" },
      ],
      itinerary: [
        { day: "Jour 1", title: "Accueil", items: ["Rassemblement à l'aéroport Mohammed V", "Accueil à l'aéroport d'Istanbul", "Transfert à l'hôtel et présentation des environs"] },
        { day: "Jour 2", title: "Pierre Loti et Balat", items: ["Colline Pierre Loti en téléphérique", "Mosquée Eyüp Sultan", "Quartier Balat", "Centre commercial Venezia"] },
        { day: "Jour 3", title: "Bosphore et Florya", items: ["Croisière privée sur le Bosphore", "Centre commercial Florya", "Aquarium Florya en option", "Centre commercial Olivium"] },
        { day: "Jour 4", title: "Journée libre", items: ["Activités optionnelles", "Shopping dans les marchés populaires"] },
        { day: "Jour 5", title: "Côté asiatique", items: ["Colline des mariées", "Mosquée Çamlıca", "Kadıköy", "Ortaköy"] },
        { day: "Jour 6", title: "Sapanca en option", items: ["Sommet Kartepe", "Terrasse en verre", "Déjeuner à Sopeli", "Lac Sapanca"] },
        { day: "Jour 7", title: "Soirée Bosphore optionnelle", items: ["Journée libre", "Soirée musicale turque", "Dîner et transfert privé"] },
        { day: "Jour 8", title: "Retour", items: ["Remise des chambres", "Transfert à l'aéroport", "Vol retour"] },
      ],
      includes: ["Billets d'avion avec Royal Air Maroc", "7 nuits en hôtel 4 étoiles", "Transfert aéroport-hôtel-aéroport", "Petits-déjeuners", "Programme de base de 3 jours gratuit", "Accompagnateur marocain", "56 kg de bagages"],
      notIncluded: ["Excursion Sapanca 300 DH", "Soirée Bosphore 390 DH", "Aquarium Florya", "Activités des journées libres"],
      notes: ["Dernier délai de réservation: 15 jours avant le départ", "Confirmation minimum: 7,000 DH", "Passeport valable 6 mois après le retour", "Assurance voyage optionnelle"],
    },
    106: {
      title: "Égypte: Le Caire + Alexandrie + Charm el-Cheikh",
      route: "Le Caire → Alexandrie → Charm el-Cheikh",
      location: "Le Caire, Alexandrie, Charm el-Cheikh",
      dates: "Juillet et août",
      badge: "Égypte",
      type: "Voyage international",
      airline: "Vols directs et internes",
      summary: "Un voyage en Égypte entre les monuments du Caire, une journée à Alexandrie et un séjour balnéaire tout compris à Charm el-Cheikh.",
      hotels: [
        { city: "Le Caire", name: "Gewan Hotel Cairo Tulip Golden Plaza", rating: "4.2★" },
        { city: "Charm el-Cheikh", name: "Sharks Bay Resort / Dreams Beach Resort", rating: "4.4★ - 5★" },
      ],
      priceTable: [
        { label: "Juillet - chambre double", value: "15,950 DH" },
        { label: "Juillet - chambre individuelle", value: "18,950 DH" },
        { label: "Août - chambre double", value: "16,450 DH" },
        { label: "Août - chambre individuelle", value: "19,450 DH" },
      ],
      itinerary: [
        { day: "Jour 1", title: "Départ", items: ["Rassemblement à l'aéroport Mohammed V", "Vol direct vers Le Caire"] },
        { day: "Jour 2", title: "Arrivée au Caire", items: ["Accueil à l'aéroport", "Petit-déjeuner égyptien traditionnel", "Installation dans les chambres", "Visite de City Star"] },
        { day: "Jour 3", title: "Pyramides et musée", items: ["Pyramides et Sphinx", "Panorama des pyramides", "Atelier de papyrus", "Grand Musée Égyptien"] },
        { day: "Jour 4", title: "Alexandrie", items: ["Citadelle de Qaitbay", "Bibliothèque d'Alexandrie", "Mosquée Al Mursi Abu Al Abbas", "Kom El Shoqafa", "Musée des bijoux royaux"] },
        { day: "Jour 5", title: "Le Caire islamique", items: ["Mosquée Al Hussein", "Mosquée Amr Ibn Al As", "Al Azhar", "Khan El Khalili et rue Al Muizz"] },
        { day: "Jour 6", title: "Charm el-Cheikh", items: ["Vol intérieur vers Charm el-Cheikh", "Installation", "Séjour en formule tout compris"] },
        { day: "Jours 7 à 9", title: "Charm el-Cheikh", items: ["Plage et piscine", "Activités nautiques optionnelles", "Ras Mohammed ou île de Tiran en option"] },
        { day: "Jour 10", title: "Retour", items: ["Petit-déjeuner", "Transfert à l'aéroport de Charm el-Cheikh", "Retour au pays"] },
      ],
      includes: ["Vol international direct vers Le Caire", "Retour international depuis Charm el-Cheikh", "Vol intérieur Le Caire - Charm el-Cheikh", "4 nuits au Caire avec petit-déjeuner", "4 nuits à Charm el-Cheikh en formule tout compris", "Journée à Alexandrie", "Visites touristiques mentionnées"],
      notIncluded: ["Frais de visa 500 DH", "Pourboires de 20 USD à l'arrivée", "Excursions optionnelles", "Dépenses personnelles"],
      notes: ["À la réservation: 50%", "À la sortie du visa: 25%", "À l'émission des billets internationaux et internes: 25%", "Offre non remboursable après obtention du visa"],
    },
  },
  en: {
    101: {
      title: "Vietnam + Thailand",
      route: "Hanoi → Da Nang → Bangkok",
      location: "Hanoi, Da Nang, Bangkok",
      dates: "20/06 to 05/07",
      badge: "Asia",
      type: "International Trip",
      airline: "Turkish Airlines - Istanbul stopover",
      summary: "A rich Asian journey combining Vietnam's culture, Da Nang's scenery, and Bangkok's energy with 4-star hotels and Turkish Airlines flights.",
      hotels: [
        { city: "Hanoi", name: "Rey Hanoi Hotel", rating: "4.5★", nights: "" },
        { city: "Da Nang", name: "Balcona Danang", rating: "4★", nights: "" },
        { city: "Bangkok", name: "Grand Mercure Bangkok Atrium", rating: "4.3★", nights: "" },
      ],
      itinerary: [
        { day: "Day 1", title: "Departure", items: ["Meet at Mohammed V Airport in Casablanca", "Stopover in Istanbul, then arrival at Hanoi Airport"] },
        { day: "Day 2", date: "21/06", title: "Arrival in Hanoi", items: ["Airport welcome", "Transfer to the hotel and check-in", "Free time to rest"] },
        { day: "Day 3", date: "22/06", title: "Hanoi City Tour", items: ["Breakfast at the hotel", "Full city tour of Hanoi", "Visit main landmarks and traditional markets"] },
        { day: "Day 4", date: "23/06", title: "Ninh Binh Excursion", items: ["Full-day trip through nature and mountain scenery", "Return to Hanoi and overnight at the hotel"] },
        { day: "Day 5", date: "24/06", title: "Ha Long Bay", items: ["Departure to Ha Long Bay", "Premium cruise and sea tour", "Lunch included"] },
        { day: "Day 6", date: "25/06", title: "Free Day", items: ["Rest and enjoy the hotel", "Optional activities can be suggested"] },
        { day: "Day 7", date: "26/06", title: "Departure to Da Nang", items: ["Airport transfer", "Arrival in Da Nang", "Welcome and transfer to the hotel"] },
        { day: "Day 8", date: "27/06", title: "Da Nang Tour", items: ["Son Tra Peninsula", "Linh Ung Pagoda", "Marble Mountains", "Han Market", "Free time for the Fireworks show"] },
        { day: "Day 9", date: "28/06", title: "Da Nang by Night", items: ["Night market visit", "Dragon Bridge show"] },
        { day: "Day 10", date: "29/06", title: "Ba Na Hills", items: ["Cable car ride", "Golden Bridge visit", "French village", "Buffet lunch at a halal restaurant"] },
        { day: "Day 11", date: "30/06", title: "Hoi An Tour", items: ["Coconut Forest", "Hoi An Ancient Town", "Hoi An Night Market", "Japanese Covered Bridge"] },
        { day: "Day 12", date: "01/07", title: "Free Day", items: ["Free day with possible activities depending on the weather"] },
        { day: "Day 13", date: "02/07", title: "Departure to Bangkok", items: ["Transfer from hotel to airport", "Arrival in Bangkok", "Free evening"] },
        { day: "Day 14", date: "03/07", title: "Bangkok", items: ["Free day in the city center", "Free evening to explore the city"] },
        { day: "Day 15", date: "04/07", title: "Departure", items: ["Check-out at 12:00", "Transfer to Bangkok International Airport"] },
        { day: "Day 16", date: "05/07", title: "Arrival in Morocco", items: ["Arrival in Morocco"] },
      ],
      includes: ["Round-trip ticket with Turkish Airlines", "Flight from Hanoi to Da Nang", "Flight from Da Nang to Bangkok", "Hotels with breakfast", "Listed tours and activities", "3 lunches", "Vietnam visa fees"],
      notIncluded: ["Meals and personal expenses", "Optional activities", "Travel insurance"],
    },
    102: {
      title: "Thailand and Phi Phi Islands",
      route: "Bangkok → Phuket → Phi Phi Islands",
      location: "Bangkok, Phuket, Phi Phi",
      dates: "Monthly dates from April to October",
      badge: "Islands and Beaches",
      type: "International Trip",
      summary: "A Thailand program combining Bangkok, Phuket, and Phi Phi Islands with sea tours, adventure experiences, and selected hotels.",
      hotels: [
        { city: "Bangkok", name: "Grand Mercure Bangkok Atrium", rating: "4.3★" },
        { city: "Phuket", name: "Novotel Phuket Vintage Park Resort", rating: "4.4★" },
        { city: "Phuket", name: "Nap Patong / Deevana Plaza", rating: "4.4★ - 4.6★" },
      ],
      priceTable: [
        { label: "April", value: "21,500 DH" },
        { label: "May", value: "21,500 DH" },
        { label: "June", value: "25,000 DH" },
        { label: "July", value: "27,000 DH" },
        { label: "August", value: "28,000 DH" },
        { label: "September", value: "28,000 DH" },
        { label: "October", value: "25,000 DH" },
      ],
      itinerary: [
        { day: "Day 1", title: "Arrival in Bangkok", items: ["Airport welcome", "Private transfer to the hotel", "Free evening"] },
        { day: "Day 2", title: "Discover Bangkok", items: ["Buddha temple", "Royal Palace", "Luxury mall", "Traditional night market"] },
        { day: "Day 3", title: "Free Day", items: ["Optional Chao Phraya River tour", "Shopping or optional activities"] },
        { day: "Day 4", title: "Travel to Phuket", items: ["Airport transfer", "Domestic flight to Phuket", "Welcome and hotel transfer"] },
        { day: "Day 5", title: "Phuket Highlights", items: ["Old Town", "Wat Chalong", "Big Buddha", "Karon View Point"] },
        { day: "Day 6", title: "Adventure Day", items: ["Safari", "Rafting", "Zipline", "Quad biking", "Elephant experience", "Lunch included"] },
        { day: "Day 7", title: "Free Day", items: ["Beach time or optional water activities"] },
        { day: "Day 8", title: "Phi Phi Islands", items: ["Speed boat transfer", "Free time on the island"] },
        { day: "Day 9", title: "Private Sea Tour", items: ["Monkey Beach", "Piley Lagoon", "Viking Cave", "Maya Bay", "Lunch included"] },
        { day: "Day 10", title: "Return to Phuket", items: ["Boat transfer", "Free evening"] },
        { day: "Day 11", title: "Departure", items: ["Private airport transfer", "Return flight to Morocco"] },
      ],
      includes: ["Casablanca to Bangkok/Phuket and return flight", "Domestic flight Bangkok to Phuket", "10 nights in the listed hotels", "Breakfasts", "2 lunches", "Round-trip boat tickets", "Listed tours", "Guide and assistance", "Air-conditioned coach"],
      notIncluded: ["Meals and personal expenses", "Free-day activities", "Travel insurance"],
    },
    103: {
      title: "Kuala Lumpur + Bali",
      route: "Kuala Lumpur → Bali → Kuala Lumpur",
      location: "Malaysia, Bali",
      dates: "Two departures per month from April to November 2026",
      badge: "Malaysia and Bali",
      type: "International Trip",
      summary: "A longer activity-filled journey between Kuala Lumpur and Bali: beaches, Ubud, Kuta, Seminyak, and rich excursions.",
      hotels: [
        { city: "Kuala Lumpur", name: "Royal Signature Bukit Bintang", rating: "5★", nights: "4 nights" },
        { city: "Bali - Kuta", name: "Discovery Kartika Plaza", rating: "5★", nights: "3 nights" },
        { city: "Bali - Ubud", name: "Swan Paradise", rating: "4★", nights: "4 nights" },
        { city: "Bali - Seminyak", name: "Jambuluwuk Oceano", rating: "5★", nights: "2 nights" },
      ],
      priceTable: [
        { label: "April", value: "21,400 DH" },
        { label: "May", value: "22,900 DH" },
        { label: "June", value: "23,900 DH" },
        { label: "July", value: "26,300 DH" },
        { label: "August", value: "26,800 DH" },
        { label: "September", value: "26,300 / 25,300 DH" },
        { label: "October", value: "22,900 DH" },
        { label: "November", value: "21,400 DH" },
      ],
      itinerary: [
        { day: "Day 1", title: "Departure", items: ["Meet at Mohammed V Airport", "International flight to Kuala Lumpur"] },
        { day: "Day 2", title: "Arrival in Kuala Lumpur", items: ["Local welcome", "Hotel transfer", "Night outing to malls and Petronas Towers"] },
        { day: "Day 3", title: "Capital City Tour", items: ["Batu Caves", "Chinese temple", "National Mosque", "Independence Square"] },
        { day: "Day 4", title: "Departure to Bali", items: ["AirAsia or BatikAir flight", "Flower necklace welcome", "Overnight in Kuta"] },
        { day: "Day 6", title: "South Bali", items: ["Tanjung Benoa", "Banana Boat", "Melasti Beach", "Kecak show", "Lunch included"] },
        { day: "Day 7", title: "Ubud Villages", items: ["Glass Bridge Bali", "Rainbow Slide", "Peliatan Royal Palace", "Taman Dedari", "Coffee plantations"] },
        { day: "Day 9", title: "Ubud Activities", items: ["Rafting on Ayung River", "D'Tukad Coffee Club", "Aloha Swing"] },
        { day: "Day 11", title: "Bedugul and Seminyak", items: ["Ulun Danu Beratan Temple", "Handara Golf Resort", "Wanagiri Hidden Hill"] },
        { day: "Day 12", title: "Optional Islands", items: ["Nusa Penida or Nusa Lembongan", "Beaches, viewpoints, and lunch depending on the excursion"] },
        { day: "Day 15", title: "Return to Morocco", items: ["Hotel departure", "Airport transfer", "Return to Morocco"] },
      ],
      includes: ["Round-trip international flight", "Round-trip Kuala Lumpur - Bali domestic flight", "13 nights in listed hotels", "Breakfasts", "Listed tours and activities", "Arabic or Moroccan Darija-speaking assistant"],
      notIncluded: ["Personal expenses", "Some optional activities", "Any service not mentioned"],
      notes: ["Deposit: 10,000 DH, and 15,000 DH in July, August, and September", "Balance due 10 days before departure", "Cancellation is not possible once the flight ticket is issued"],
    },
    104: {
      title: "Maldives + Sri Lanka + Istanbul",
      route: "Maldives → Sri Lanka → Istanbul",
      location: "Maldives, Sri Lanka, Istanbul",
      dates: "30/04 to 10/05",
      badge: "5-Star Hotels",
      type: "International Trip",
      airline: "Multi-destination international flight",
      summary: "A premium program combining Maldives relaxation, Sri Lanka nature and adventure, and a final Istanbul stop.",
      hotels: [{ city: "All destinations", name: "Selected 5-star hotels", rating: "5★", nights: "" }],
      itinerary: [
        { day: "Day 1", title: "Arrival in the Maldives", items: ["Arrival at Maldives International Airport", "Speed boat to MARINA island", "Free time"] },
        { day: "Days 2 to 4", title: "Free Days on the Island", items: ["Enjoy the island, beach, and resort"] },
        { day: "Day 5", title: "Departure to Sri Lanka", items: ["Speed boat to the airport", "Arrival in Sri Lanka", "Colombo city tour"] },
        { day: "Day 6", title: "Colombo to Dambulla", items: ["MAHA RAJA ELEPHANT VILLAGE", "Tuk Tuk", "Arrival at Dambulla hotel"] },
        { day: "Day 7", title: "Habarana and Safari", items: ["Habarana Old Village Tour", "Bullock Cart Ride", "Lake Rafting", "Safari among elephants"] },
        { day: "Day 8", title: "Dambulla to Colombo", items: ["Ayurvedic Herbal Garden", "Kandy Lake", "Gem Museum"] },
        { day: "Day 9", title: "Colombo", items: ["Colombo city tour"] },
        { day: "Day 10", title: "Istanbul", items: ["Transfer from airport to hotel", "Free time"] },
        { day: "Day 11", title: "Return to Morocco", items: ["Return flight"] },
      ],
      includes: ["5-star hotels", "Breakfast and dinner", "Listed transfers", "Tours mentioned in the program"],
      notIncluded: ["Personal expenses", "Optional activities", "Any service not mentioned"],
    },
    105: {
      title: "Istanbul",
      route: "Casablanca → Istanbul → Casablanca",
      location: "Istanbul",
      dates: "Several dates from April to July",
      badge: "3 Free Tours",
      type: "International Trip",
      airline: "Royal Air Maroc",
      summary: "An 8-day Istanbul program with a 4-star hotel, Royal Air Maroc flights, 56 kg baggage allowance, and three included tours.",
      hotels: [{ city: "Istanbul", name: "Grand Asiyan Hotel", rating: "4★", location: "Laleli, 200 m from the tram station, indoor pool and Turkish hammam" }],
      priceTable: [
        { label: "12/04 → 19/04", value: "7,990 DH" },
        { label: "25/04 → 02/05", value: "8,290 DH" },
        { label: "01/05 → 08/05", value: "8,700 DH" },
        { label: "02/05 → 09/05", value: "8,900 DH" },
        { label: "17/05 → 24/05", value: "8,190 DH" },
        { label: "21/06 → 28/06", value: "10,390 DH" },
        { label: "28/06 → 05/07", value: "10,390 DH" },
      ],
      itinerary: [
        { day: "Day 1", title: "Welcome", items: ["Meet at Mohammed V Airport", "Welcome at Istanbul Airport", "Transfer to hotel and introduction to the surroundings"] },
        { day: "Day 2", title: "Pierre Loti and Balat", items: ["Pierre Loti hill by cable car", "Eyup Sultan Mosque", "Balat district", "Venezia Mall"] },
        { day: "Day 3", title: "Bosphorus and Florya", items: ["Private Bosphorus cruise", "Florya Mall", "Florya Aquarium optional", "Olivium Mall"] },
        { day: "Day 4", title: "Free Day", items: ["Optional activities", "Shopping in popular markets"] },
        { day: "Day 5", title: "Asian Side", items: ["Camlica Hill", "Camlica Mosque", "Kadikoy", "Ortakoy"] },
        { day: "Day 6", title: "Optional Sapanca", items: ["Kartepe summit", "Glass terrace", "Lunch in Sopeli", "Sapanca Lake"] },
        { day: "Day 7", title: "Optional Bosphorus Evening", items: ["Free day", "Turkish music evening", "Dinner and private transfer"] },
        { day: "Day 8", title: "Return", items: ["Room handover", "Airport transfer", "Return flight"] },
      ],
      includes: ["Flight tickets with Royal Air Maroc", "7 nights in a 4-star hotel", "Airport-hotel-airport transfers", "Breakfasts", "Free 3-day basic program", "Moroccan assistant", "56 kg baggage allowance"],
      notIncluded: ["Sapanca excursion 300 DH", "Bosphorus evening 390 DH", "Florya Aquarium", "Free-day activities"],
      notes: ["Booking deadline: 15 days before departure", "Minimum confirmation: 7,000 DH", "Passport valid 6 months after return", "Travel insurance optional"],
    },
    106: {
      title: "Egypt: Cairo + Alexandria + Sharm El Sheikh",
      route: "Cairo → Alexandria → Sharm El Sheikh",
      location: "Cairo, Alexandria, Sharm El Sheikh",
      dates: "July and August",
      badge: "Egypt",
      type: "International Trip",
      airline: "Direct international and domestic flights",
      summary: "An Egyptian journey combining Cairo's monuments, an Alexandria day trip, and an all-inclusive beach stay in Sharm El Sheikh.",
      hotels: [
        { city: "Cairo", name: "Gewan Hotel Cairo Tulip Golden Plaza", rating: "4.2★" },
        { city: "Sharm El Sheikh", name: "Sharks Bay Resort / Dreams Beach Resort", rating: "4.4★ - 5★" },
      ],
      priceTable: [
        { label: "July - double room", value: "15,950 DH" },
        { label: "July - single room", value: "18,950 DH" },
        { label: "August - double room", value: "16,450 DH" },
        { label: "August - single room", value: "19,450 DH" },
      ],
      itinerary: [
        { day: "Day 1", title: "Departure", items: ["Meet at Mohammed V Airport", "Direct flight to Cairo"] },
        { day: "Day 2", title: "Arrival in Cairo", items: ["Airport welcome", "Traditional Egyptian breakfast", "Room check-in", "Visit to City Star"] },
        { day: "Day 3", title: "Pyramids and Museum", items: ["Pyramids and Sphinx", "Pyramids panorama", "Papyrus workshop", "Grand Egyptian Museum"] },
        { day: "Day 4", title: "Alexandria", items: ["Qaitbay Citadel", "Bibliotheca Alexandrina", "Al Mursi Abu Al Abbas Mosque", "Kom El Shoqafa", "Royal Jewelry Museum"] },
        { day: "Day 5", title: "Islamic Cairo", items: ["Al Hussein Mosque", "Amr Ibn Al As Mosque", "Al Azhar", "Khan El Khalili and Al Muizz Street"] },
        { day: "Day 6", title: "Sharm El Sheikh", items: ["Domestic flight to Sharm El Sheikh", "Check-in", "All-inclusive stay"] },
        { day: "Days 7 to 9", title: "Sharm El Sheikh", items: ["Beach and pool", "Optional water activities", "Ras Mohammed or Tiran Island optional"] },
        { day: "Day 10", title: "Return", items: ["Breakfast", "Transfer to Sharm El Sheikh Airport", "Return home"] },
      ],
      includes: ["Direct international flight to Cairo", "International return from Sharm El Sheikh", "Domestic flight Cairo - Sharm El Sheikh", "4 nights in Cairo with breakfast", "4 nights in Sharm El Sheikh all-inclusive", "Alexandria day trip", "Listed sightseeing tours"],
      notIncluded: ["500 DH visa fees", "20 USD tips collected on arrival", "Optional excursions", "Personal expenses"],
      notes: ["At booking: 50%", "When visa is issued: 25%", "When international and domestic tickets are issued: 25%", "Offer is non-refundable after visa approval"],
    },
  },
};

const dayVisuals = {
  101: [
    "https://images.unsplash.com/photo-1528127269322-539801943592?w=900&q=80",
    "https://images.unsplash.com/photo-1504457047772-27faf1c00561?w=900&q=80",
    "https://images.unsplash.com/photo-1528127269322-539801943592?w=900&q=80",
    "https://images.unsplash.com/photo-1559592413-7cec4d0cae2b?w=900&q=80",
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=900&q=80",
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=900&q=80",
    "https://images.unsplash.com/photo-1559592413-7cec4d0cae2b?w=900&q=80",
    "https://images.unsplash.com/photo-1506812574058-fc75fa93fead?w=900&q=80",
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=900&q=80",
    "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=900&q=80",
    "https://images.unsplash.com/photo-1544644181-1484b3fdfc62?w=900&q=80",
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=900&q=80",
    "https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=900&q=80",
  ],
  102: [
    "https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=900&q=80",
    "https://images.unsplash.com/photo-1528181304800-259b08848526?w=900&q=80",
    "https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=900&q=80",
    "https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?w=900&q=80",
    "https://images.unsplash.com/photo-1504214208698-ea1916a2195a?w=900&q=80",
  ],
  103: [
    "https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=900&q=80",
    "https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=900&q=80",
    "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=900&q=80",
    "https://images.unsplash.com/photo-1508964942454-1a56651d54ac?w=900&q=80",
    "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=900&q=80",
  ],
  104: [
    "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=900&q=80",
    "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=900&q=80",
    "https://images.unsplash.com/photo-1546708973-b339540b5162?w=900&q=80",
    "https://images.unsplash.com/photo-1546708973-b339540b5162?w=900&q=80",
    "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=900&q=80",
  ],
  105: [
    "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=900&q=80",
    "https://images.unsplash.com/photo-1541432901042-2d8bd64b4a9b?w=900&q=80",
    "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=900&q=80",
    "https://images.unsplash.com/photo-1510253687831-0f982d7862fc?w=900&q=80",
  ],
  106: [
    "https://images.unsplash.com/photo-1539650116574-8efeb43e2750?w=900&q=80",
    "https://images.unsplash.com/photo-1572252009286-268acec5ca0a?w=900&q=80",
    "https://images.unsplash.com/photo-1539650116574-8efeb43e2750?w=900&q=80",
    "https://images.unsplash.com/photo-1572252009286-268acec5ca0a?w=900&q=80",
    "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=900&q=80",
  ],
};

const PackageDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = ["ar", "fr", "en"].includes((i18n.language || "ar").split("-")[0]) ? (i18n.language || "ar").split("-")[0] : "ar";
  const copy = uiCopy[lang] || uiCopy.ar;
  const editableProgrammes = useEditableContent("programmes.json", FALLBACK_PROGRAMMES);
  const umrahProgrammes = Array.isArray(editableProgrammes) ? editableProgrammes : FALLBACK_PROGRAMMES;
  const allProgrammes = [...umrahProgrammes, ...internationalProgrammes];
  const programme = useMemo(() => allProgrammes.find((item) => String(item.id) === String(id)), [allProgrammes, id]);
  const localized = {
    ...(localizedProgramCopy[programme?.id]?.[lang] || {}),
    ...(internationalDetailCopy[lang]?.[programme?.id] || {}),
  };
  const viewProgramme = programme ? { ...programme, ...localized } : null;
  const text = (field) => viewProgramme?.[field] || "";

  if (!programme) {
    return (
      <>
        <PageHeader tag={t("nav.packages")} title={copy.notFoundTitle} subtitle={copy.notFoundSubtitle} />
        <div style={{ padding: "60px 20px", textAlign: "center" }}>
          <button className="btn-gold" onClick={() => navigate("/packages")}>{copy.backPackages}</button>
        </div>
      </>
    );
  }

  const isInternational = programme.cat === "international";
  const gallery = Array.isArray(programme.gallery) && programme.gallery.length > 0 ? programme.gallery : [programme.img];
  const roomPrices = programme.roomPrices || [];
  const services = programme.services || [];
  const notIncluded = programme.notIncluded || [];
  const faq = programme.faq || [];
  const hotelInfo = programme.hotelInfo || {};

  const openWhatsApp = () => {
    const message = encodeURIComponent(`${copy.whatsappMessage} ${text("title") || programme.title}`);
    window.open(`https://wa.me/212640595665?text=${message}`, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <style>{`
        .detail-wrap { background:#fbf8f1; padding:54px 56px 86px; }
        .detail-shell { max-width:1180px; margin:0 auto; display:grid; gap:26px; }
        .detail-gallery { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:14px; direction:ltr; }
        .gallery-frame { aspect-ratio:4/3; height:clamp(190px,24vw,300px); border-radius:18px; overflow:hidden; background:#e7decd; box-shadow:0 16px 38px rgba(13,27,42,.08); }
        .gallery-frame.small { aspect-ratio:4/3; height:clamp(190px,24vw,300px); }
        .gallery-frame img { width:100%; height:100%; object-fit:cover; display:block; }
        .detail-card { background:#fff; border:1px solid rgba(13,72,51,.08); border-radius:18px; box-shadow:0 14px 38px rgba(13,27,42,.07); padding:26px; }
        .summary-grid { display:grid; grid-template-columns:minmax(0,1fr) 320px; gap:26px; align-items:start; }
        .detail-title-row { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom:14px; }
        .detail-title { color:#0d1b2a; font-size:32px; line-height:1.35; margin:0; }
        .detail-badge { border-radius:999px; background:#0d4833; color:#fff; padding:8px 13px; font-weight:800; font-size:12px; white-space:nowrap; }
        .detail-text { color:#5f6d67; line-height:1.8; font-size:15px; margin-bottom:20px; }
        .info-strip { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-top:20px; }
        .info-box { background:#faf6ee; border:1px solid #eee3cf; border-radius:14px; padding:14px; }
        .info-label { display:block; color:#a07830; font-size:11px; font-weight:900; margin-bottom:5px; }
        .info-value { color:#0d4833; font-weight:800; line-height:1.45; }
        .price-panel { background:linear-gradient(160deg,#0d4833,#0d1b2a); color:#fff; border-radius:18px; padding:24px; box-shadow:0 20px 46px rgba(13,72,51,.2); }
        .price-panel small { color:#e8c97a; font-weight:900; }
        .price-panel strong { display:block; font-family:var(--font-heading); font-size:42px; line-height:1.15; margin:5px 0 18px; }
        .whatsapp-btn { width:100%; border:0; border-radius:12px; min-height:48px; background:linear-gradient(135deg,#c9a84c,#a07830); color:#fff; font-family:var(--font-ui); font-weight:900; cursor:pointer; box-shadow:0 14px 28px rgba(160,120,48,.24); }
        .section-heading { font-size:23px; color:#0d1b2a; margin-bottom:18px; }
        .service-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:12px; }
        .service-pill { display:flex; gap:10px; align-items:flex-start; background:#fbf8f1; border:1px solid #eee6d7; border-radius:13px; padding:12px 14px; color:#46554f; font-size:14px; line-height:1.55; }
        .service-pill span:first-child { color:#0d7a4f; font-weight:900; }
        .hotel-card-grid { display:grid; grid-template-columns:minmax(0,1fr) 320px; gap:22px; align-items:start; }
        .hotel-photo-frame { width:100%; aspect-ratio:4/3; height:300px; max-height:300px; border-radius:16px; overflow:hidden; background:#e7decd; }
        .hotel-photo-frame img { width:100%; height:100%; object-fit:cover; display:block; }
        .hotel-info-list { display:grid; gap:10px; align-content:start; }
        .hotel-feature { display:flex; gap:10px; color:#46554f; font-size:14px; line-height:1.6; background:#fbf8f1; border:1px solid #eee6d7; border-radius:13px; padding:11px 13px; }
        .hotel-feature span:first-child { color:#a07830; font-weight:900; }
        .not-included span:first-child { color:#a07830; }
        .price-table { width:100%; border-collapse:collapse; overflow:hidden; border-radius:14px; font-size:14px; }
        .price-table th { background:#0d4833; color:#fff; text-align:start; padding:14px; }
        .price-table td { padding:14px; border-bottom:1px solid #efe7d9; color:#46554f; }
        .price-table tr:last-child td { border-bottom:0; }
        .price-table td:last-child { color:#0d4833; font-weight:900; }
        .faq-list { display:grid; gap:12px; }
        .faq-item { border:1px solid #eee6d7; border-radius:14px; padding:16px; background:#fffdf8; }
        .faq-q { color:#0d4833; font-weight:900; margin-bottom:6px; }
        .faq-a { color:#66756e; line-height:1.7; font-size:14px; }
        .detail-actions { display:flex; gap:10px; flex-wrap:wrap; margin-top:18px; }
        .back-btn { border:1px solid rgba(13,72,51,.18); background:#fff; color:#0d4833; border-radius:12px; padding:13px 18px; font-family:var(--font-ui); font-weight:900; cursor:pointer; }
        .highlight-grid { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:14px; }
        .highlight-card { height:220px; min-height:220px; border-radius:16px; overflow:hidden; position:relative; background:#0d1b2a; box-shadow:0 12px 30px rgba(13,27,42,.1); }
        .highlight-card img { width:100%; height:100%; object-fit:cover; display:block; position:absolute; inset:0; transition:transform .45s ease; }
        .highlight-card::after { content:""; position:absolute; inset:0; background:linear-gradient(180deg,rgba(0,0,0,.08),rgba(13,27,42,.68)); }
        .highlight-card:hover img { transform:scale(1.05); }
        .highlight-content { position:absolute; inset-inline:16px; bottom:15px; z-index:1; color:#fff; }
        .highlight-kicker { display:inline-block; background:rgba(232,201,122,.95); color:#0d4833; border-radius:999px; padding:5px 10px; font-size:11px; font-weight:900; margin-bottom:9px; }
        .highlight-title { font-weight:900; line-height:1.45; }
        .itinerary-list { display:grid; gap:16px; }
        .itinerary-day { background:#fffdf8; border:1px solid #eee6d7; border-radius:16px; overflow:hidden; display:grid; grid-template-columns:240px minmax(0,1fr); box-shadow:0 10px 28px rgba(13,27,42,.05); }
        .itinerary-img { height:210px; min-height:210px; background:#e7decd; }
        .itinerary-img img { width:100%; height:100%; object-fit:cover; display:block; }
        .itinerary-content { padding:18px 20px; }
        .itinerary-day-title { color:#0d4833; font-weight:900; margin-bottom:10px; font-size:16px; line-height:1.55; }
        .itinerary-day-items { color:#5f6d67; font-size:14px; line-height:1.7; margin:0; }
        .itinerary-day-items li { margin-bottom:6px; }
        .hotels-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px; }
        .hotel-item { background:#fbf8f1; border:1px solid #eee6d7; border-radius:12px; padding:16px; }
        .hotel-item-city { color:#a07830; font-weight:900; font-size:12px; margin-bottom:6px; }
        .hotel-item-name { color:#0d4833; font-weight:800; margin-bottom:6px; }
        .hotel-item-meta { color:#7a817d; font-size:13px; display:flex; justify-content:space-between; }
        .notes-list { color:#5f6d67; font-size:14px; line-height:1.7; }
        .notes-list li { margin-bottom:10px; list-style-position:inside; }
        @media(max-width:900px){ .detail-wrap{padding:38px 18px 64px;} .summary-grid{grid-template-columns:1fr;} .detail-gallery{grid-template-columns:1fr 1fr;} .info-strip{grid-template-columns:1fr 1fr;} .service-grid{grid-template-columns:1fr;} .hotel-card-grid{grid-template-columns:1fr;} .hotel-photo-frame{height:260px; max-height:260px;} .detail-title{font-size:26px;} .hotels-grid{grid-template-columns:1fr;} .highlight-grid{grid-template-columns:repeat(2,minmax(0,1fr));} .itinerary-day{grid-template-columns:1fr;} .itinerary-img{height:220px; min-height:220px;} }
        @media(max-width:560px){ .info-strip{grid-template-columns:1fr;} .detail-gallery{grid-template-columns:1fr;} .gallery-frame,.gallery-frame.small{aspect-ratio:16/10; height:220px;} .hotel-photo-frame{height:220px; max-height:220px;} .highlight-grid{grid-template-columns:1fr;} .highlight-card{height:200px; min-height:200px;} }
      `}</style>

      <PageHeader tag={text("badge") || text("type")} title={text("title")} subtitle={text("location")} />

      <section className="detail-wrap">
        <div className="detail-shell">
          {isInternational ? (
            <>
              <div className="summary-grid">
                <div className="detail-card">
                  <div className="detail-title-row">
                    <h2 className="detail-title">{text("title")}</h2>
                    <span className="detail-badge">{text("badge") || copy.internationalTrip}</span>
                  </div>
                  <p className="detail-text">{text("summary")}</p>
                  <div className="info-strip">
                    <div className="info-box"><span className="info-label">{copy.duration}</span><span className="info-value">{programme.days} {copy.days}</span></div>
                    <div className="info-box"><span className="info-label">{copy.route}</span><span className="info-value">{text("route")}</span></div>
                    <div className="info-box"><span className="info-label">{copy.dates}</span><span className="info-value">{text("dates")}</span></div>
                    <div className="info-box"><span className="info-label">{copy.airline}</span><span className="info-value">{text("airline")}</span></div>
                  </div>
                  <div className="detail-actions">
                    <button className="back-btn" onClick={() => navigate("/packages")}>{copy.backPackages}</button>
                    <button className="btn-gold" onClick={() => navigate("/contact")}>{copy.bookNow}</button>
                  </div>
                </div>

                <aside className="price-panel">
                  <small>{copy.from}</small>
                  <strong>{formatPrice(programme.price)} DH</strong>
                  <button className="whatsapp-btn" onClick={openWhatsApp}>{copy.whatsapp}</button>
                </aside>
              </div>

              <div className="detail-gallery" aria-label={copy.tripPhotos}>
                {gallery.map((img, idx) => (
                  <div key={idx} className={`gallery-frame ${idx > 0 ? "small" : ""}`}>
                    <img src={img} alt={`${text("title")} - ${copy.tripPhotos} ${idx + 1}`} />
                  </div>
                ))}
              </div>

              {viewProgramme.itinerary && viewProgramme.itinerary.length > 0 && (
                <div className="detail-card">
                  <h3 className="section-heading">{copy.highlights}</h3>
                  <div className="highlight-grid">
                    {viewProgramme.itinerary.slice(0, 4).map((day, idx) => {
                      const visual = day.image || dayVisuals[programme.id]?.[idx] || gallery[idx % gallery.length];
                      return (
                        <div key={idx} className="highlight-card">
                          <img src={visual} alt={day.title} loading="lazy" />
                          <div className="highlight-content">
                            <span className="highlight-kicker">{day.day}</span>
                            <div className="highlight-title">{day.title}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {viewProgramme.hotels && viewProgramme.hotels.length > 0 && (
                <div className="detail-card">
                  <h3 className="section-heading">{copy.selectedHotels}</h3>
                  <div className="hotels-grid">
                    {viewProgramme.hotels.map((hotel, idx) => (
                      <div key={idx} className="hotel-item">
                        <div className="hotel-item-city">{hotel.city}</div>
                        <div className="hotel-item-name">{hotel.name}</div>
                        <div className="hotel-item-meta">
                          <span>{hotel.rating}</span>
                          {hotel.nights && <span>{hotel.nights}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewProgramme.itinerary && viewProgramme.itinerary.length > 0 && (
                <div className="detail-card">
                  <h3 className="section-heading">{copy.dailyProgram}</h3>
                  <div className="itinerary-list">
                    {viewProgramme.itinerary.map((day, idx) => {
                      const visual = day.image || dayVisuals[programme.id]?.[idx % (dayVisuals[programme.id]?.length || 1)] || gallery[idx % gallery.length];
                      return (
                        <div key={idx} className="itinerary-day">
                          <div className="itinerary-img">
                            <img src={visual} alt={day.title} loading="lazy" />
                          </div>
                          <div className="itinerary-content">
                            <div className="itinerary-day-title">
                              {day.day} {day.date && `(${day.date})`}: {day.title}
                            </div>
                            <ul className="itinerary-day-items">
                              {(day.items || []).map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {viewProgramme.priceTable && viewProgramme.priceTable.length > 0 && (
                <div className="detail-card">
                  <h3 className="section-heading">{copy.pricesByMonth}</h3>
                  <table className="price-table">
                    <thead><tr><th>{copy.monthDate}</th><th>{copy.price}</th></tr></thead>
                    <tbody>
                      {viewProgramme.priceTable.map((row, idx) => (
                        <tr key={idx}>
                          <td>{row.label}</td>
                          <td>{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {viewProgramme.includes && viewProgramme.includes.length > 0 && (
                <div className="detail-card">
                  <h3 className="section-heading">{copy.included}</h3>
                  <div className="service-grid">
                    {viewProgramme.includes.map((item, idx) => (
                      <div key={idx} className="service-pill">
                        <span>✓</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewProgramme.notIncluded && viewProgramme.notIncluded.length > 0 && (
                <div className="detail-card">
                  <h3 className="section-heading">{copy.notIncluded}</h3>
                  <div className="service-grid">
                    {viewProgramme.notIncluded.map((item, idx) => (
                      <div key={idx} className="service-pill not-included">
                        <span>•</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewProgramme.notes && viewProgramme.notes.length > 0 && (
                <div className="detail-card">
                  <h3 className="section-heading">{copy.notes}</h3>
                  <ul className="notes-list">
                    {viewProgramme.notes.map((note, idx) => (
                      <li key={idx}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="summary-grid">
                <div className="detail-card">
                  <div className="detail-title-row">
                    <h2 className="detail-title">{programme.title}</h2>
                    <span className="detail-badge">{programme.badge || programme.type}</span>
                  </div>
                  <p className="detail-text">{programme.programDetails}</p>
                  <div className="info-strip">
                    <div className="info-box"><span className="info-label">{copy.duration}</span><span className="info-value">{programme.days} {copy.days}</span></div>
                    <div className="info-box"><span className="info-label">{copy.hotelCategory}</span><span className="info-value">{programme.hotelLevel || hotelInfo.category || programme.type}</span></div>
                    <div className="info-box"><span className="info-label">{copy.makkahHotel}</span><span className="info-value">{programme.makkahHotel}</span></div>
                    <div className="info-box"><span className="info-label">{copy.madinahHotel}</span><span className="info-value">{programme.madinahHotel}</span></div>
                  </div>
                  <div className="detail-actions">
                    <button className="back-btn" onClick={() => navigate("/packages")}>{copy.backPackages}</button>
                    <button className="btn-gold" onClick={() => navigate("/contact")}>{copy.bookNow}</button>
                  </div>
                </div>

                <aside className="price-panel">
                  <small>{copy.from}</small>
                  <strong>{formatPrice(programme.price)} DH</strong>
                  <button className="whatsapp-btn" onClick={openWhatsApp}>{copy.whatsapp}</button>
                </aside>
              </div>

              <div className="detail-gallery" aria-label={copy.hotelPhotos}>
                <div className="gallery-frame"><img src={gallery[0]} alt={programme.title} /></div>
                <div className="gallery-frame small"><img src={gallery[1] || gallery[0]} alt={programme.makkahHotel} /></div>
                <div className="gallery-frame small"><img src={gallery[2] || gallery[0]} alt={programme.madinahHotel} /></div>
              </div>

              <div className="detail-card">
                <h3 className="section-heading">{copy.hotelInfo}</h3>
                <div className="hotel-card-grid">
                  <div>
                    <p className="detail-text">{hotelInfo.description || programme.programDetails}</p>
                    <div className="hotel-info-list">
                      <div className="hotel-feature"><span>★</span><span>{copy.category}: {hotelInfo.category || programme.hotelLevel || programme.type}</span></div>
                      <div className="hotel-feature"><span>★</span><span>{copy.makkah}: {hotelInfo.makkah || programme.makkahHotel}</span></div>
                      <div className="hotel-feature"><span>★</span><span>{copy.madinah}: {hotelInfo.madinah || programme.madinahHotel}</span></div>
                      <div className="hotel-feature"><span>★</span><span>{copy.meals}: {hotelInfo.mealPlan || programme.mealPlan}</span></div>
                      {(hotelInfo.features || []).map((feature) => (
                        <div className="hotel-feature" key={feature}><span>✓</span><span>{feature}</span></div>
                      ))}
                    </div>
                  </div>
                  <div className="hotel-photo-frame">
                    <img src={gallery[1] || programme.img} alt={hotelInfo.category || programme.title} />
                  </div>
                </div>
              </div>

              <div className="detail-card">
                <h3 className="section-heading">{copy.included}</h3>
                <div className="service-grid">
                  {services.map((item) => <div className="service-pill" key={item}><span>✓</span><span>{item}</span></div>)}
                </div>
              </div>

              <div className="detail-card">
                <h3 className="section-heading">{copy.roomPrices}</h3>
                <table className="price-table">
                  <thead><tr><th>{copy.roomType}</th><th>{copy.personPrice}</th></tr></thead>
                  <tbody>
                    {roomPrices.map((row) => <tr key={row.room}><td>{row.room}</td><td>{row.price}</td></tr>)}
                  </tbody>
                </table>
              </div>

              <div className="detail-card">
                <h3 className="section-heading">{copy.notIncluded}</h3>
                <div className="service-grid">
                  {notIncluded.map((item) => <div className="service-pill not-included" key={item}><span>•</span><span>{item}</span></div>)}
                </div>
              </div>

              <div className="detail-card">
                <h3 className="section-heading">{copy.faq}</h3>
                <div className="faq-list">
                  {faq.map((item) => (
                    <div className="faq-item" key={item.q}>
                      <div className="faq-q">{item.q}</div>
                      <div className="faq-a">{item.a}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default PackageDetailsPage;
